
import Mixins from '@/shared'
import MatchReport from '@/components/MatchReport.component.vue'

type SeasonGroup = {
  id: string
  name: string
  gender?: string
  cup?: boolean
  competitionId: string
  competitionName: string
  teams?: Array<{ teamId: string; name: string; clubId?: string }>
}

export default {
  name: 'SeasonDashboard',
  components: {
    MatchReport
  },
  mixins: [Mixins],
  data () {
    return {
      loading: false,
      seasons: [] as any[],
      seasonGroups: [] as SeasonGroup[],
      uniqueTeams: [] as Array<{ teamId: string; name: string; clubId?: string }>,
      loadToken: 0,
      filterGender: '' as string,
      filterFrom: '' as string,
      filterTo: '' as string,
      fromMenu: false,
      toMenu: false,
      liveDialog: false
    }
  },
  computed: {
    selectedId (): string | null {
      return this.$store.state.SeasonModule.selectedId
    },
    selectedSeason (): any {
      return this.$store.getters['SeasonModule/selected']
    },
    showSeasonPicker (): boolean {
      return this.$route.name !== 'CompetitionsDashboard'
    },
    genderOptions (): Array<{ text: string; value: string }> {
      const settings = this.$store.getters['SettingsModule/getSetting']('gender') || []
      const options = [{ text: this.$t('all') as string, value: '' }]
      settings.forEach((g: string) => {
        options.push({ text: this.$t(g) as string, value: g })
      })
      return options
    },
    totalMatches (): number {
      let total = 0
      this.seasonGroups.forEach((g) => {
        total += this.$store.getters['MatchModule/filterByGroup'](g.id).length
      })
      return total
    },
    playedCount (): number {
      let total = 0
      this.seasonGroups.forEach((g) => {
        total += this.$store.getters['MatchModule/filterByGroupAndPlayed'](g.id).length
      })
      return total
    },
    playedPercent (): number {
      if (!this.totalMatches) return 0
      return Math.round((this.playedCount / this.totalMatches) * 100)
    },
    liveMatches (): any[] {
      const rows: any[] = []
      this.seasonGroups.forEach((g) => {
        const matches = this.$store.getters['MatchModule/filterByGroup'](g.id) as any[]
        matches.forEach((m) => {
          if (m.state !== 'ON_GOING') return
          rows.push({
            ...m,
            groupId: g.id,
            groupName: g.name,
            groupGender: g.gender,
            competitionId: g.competitionId,
            competitionName: g.competitionName || m.competitionName
          })
        })
      })
      rows.sort((a, b) => {
        const da = a.date && a.date.seconds ? a.date.seconds : 0
        const db = b.date && b.date.seconds ? b.date.seconds : 0
        if (da !== db) return da - db
        return String(a.time || '').localeCompare(String(b.time || ''))
      })
      return rows
    },
    liveCount (): number {
      return this.liveMatches.length
    },
    allUpcoming (): any[] {
      const rows: any[] = []
      this.seasonGroups.forEach((g) => {
        const matches = this.$store.getters['MatchModule/filterByGroup'](g.id) as any[]
        matches.forEach((m) => {
          if (m.state === 'APPROVED_BY_REFEREE') return
          rows.push({
            ...m,
            groupId: g.id,
            groupName: g.name,
            groupGender: g.gender,
            competitionId: g.competitionId,
            competitionName: g.competitionName || m.competitionName
          })
        })
      })
      rows.sort((a, b) => {
        const da = a.date && a.date.seconds ? a.date.seconds : 0
        const db = b.date && b.date.seconds ? b.date.seconds : 0
        if (da !== db) return da - db
        return String(a.time || '').localeCompare(String(b.time || ''))
      })
      return rows
    },
    filteredUpcoming (): any[] {
      let from = this.filterFrom
      let to = this.filterTo
      if (from && to && from > to) {
        const tmp = from
        from = to
        to = tmp
      }
      const fromSec = from ? Math.floor(new Date(`${from}T00:00:00`).getTime() / 1000) : null
      const toSec = to ? Math.floor(new Date(`${to}T23:59:59`).getTime() / 1000) : null

      return this.allUpcoming.filter((m) => {
        if (this.filterGender) {
          const gender = m.groupGender || (m.group && m.group.gender) || ''
          if (gender !== this.filterGender) return false
        }
        const sec = m.date && m.date.seconds != null ? m.date.seconds : null
        if (sec == null) return false
        if (fromSec != null && sec < fromSec) return false
        if (toSec != null && sec > toSec) return false
        return true
      })
    },
    upcomingHeaders (): Array<{ text: string; value: string; sortable?: boolean }> {
      return [
        { text: this.$t('date') as string, value: 'date' },
        { text: this.$t('time') as string, value: 'time', sortable: false },
        { text: this.$t('competition') as string, value: 'competitionName', sortable: false },
        { text: this.$t('group') as string, value: 'groupName', sortable: false },
        { text: this.$t('home') as string, value: 'homeTeam', sortable: false },
        { text: this.$t('away') as string, value: 'awayTeam', sortable: false },
        { text: this.$t('venue') as string, value: 'venue', sortable: false },
        { text: '', value: 'actions', sortable: false }
      ]
    }
  },
  watch: {
    selectedId: {
      immediate: true,
      handler (id: string | null) {
        if (id) {
          this.resetDateFilters()
          this.loadSeason(id)
        } else {
          this.seasonGroups = []
          this.uniqueTeams = []
        }
      }
    }
  },
  created () {
    this.$store.dispatch('SeasonModule/ensureSelected').then(() => {
      this.seasons = this.$store.getters['SeasonModule/sortedASC']
    })
  },
  methods: {
    onSeasonSelect (id: string) {
      if (id && id !== this.selectedId) {
        this.$store.dispatch('SeasonModule/setSelectedId', id)
      }
    },
    openLiveMatches () {
      if (this.liveCount > 0) {
        this.liveDialog = true
      }
    },
    resetDateFilters () {
      const today = new Date()
      const to = new Date()
      to.setDate(to.getDate() + 14)

      const season = this.selectedSeason
      if (season && season.end && season.end.seconds) {
        const endMs = season.end.seconds * 1000
        if (to.getTime() > endMs) {
          to.setTime(endMs)
        }
      }

      this.filterFrom = this.toIsoDate(today)
      this.filterTo = this.toIsoDate(to)
      this.filterGender = ''
    },
    toIsoDate (d: Date): string {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    groupRoute (item: any) {
      return {
        name: 'GroupPage',
        params: {
          competitionId: item.competitionId,
          groupId: item.groupId
        }
      }
    },
    async loadSeason (seasonId: string) {
      const token = ++this.loadToken
      this.loading = true
      try {
        await this.$store.dispatch('CompetitionTypesModule/fetchAndAdd', {
          where: [['seasonId', '==', seasonId]]
        })
        if (token !== this.loadToken) return
        await this.$store.dispatch('CompetitionTypesModule/filterBySeason', { season: seasonId })
        if (token !== this.loadToken) return

        const types = this.$store.state.CompetitionTypesModule.filteredList || []
        const competitions: any[] = []

        for (const type of types) {
          await this.$store.dispatch('CompetitonsModule/fetchAndAdd', {
            where: [['typeId', '==', type.id]]
          })
          if (token !== this.loadToken) return
          const comps = this.$store.getters['CompetitonsModule/filterByType'](type.id) || []
          competitions.push(...comps)
        }

        const groups: SeasonGroup[] = []
        for (const comp of competitions) {
          await this.$store.dispatch('GroupModule/fetchAndAdd', {
            where: [['competitionId', '==', comp.id]]
          })
          if (token !== this.loadToken) return
          const groupList = this.$store.getters['GroupModule/filterByCompetition'](comp.id) || []
          groupList.forEach((g: any) => {
            groups.push({
              id: g.id,
              name: g.name,
              gender: g.gender,
              cup: g.cup,
              competitionId: comp.id,
              competitionName: comp.name,
              teams: Array.isArray(g.teams) ? g.teams : []
            })
          })
        }

        // Load matches in small parallel batches
        const batchSize = 5
        for (let i = 0; i < groups.length; i += batchSize) {
          const batch = groups.slice(i, i + batchSize)
          await Promise.all(batch.map(g =>
            this.$store.dispatch('MatchModule/fetchAndAdd', {
              clauses: {
                where: [['groupId', '==', g.id]],
                limit: 0
              }
            })
          ))
          if (token !== this.loadToken) return
        }

        const teamMap: Record<string, { teamId: string; name: string; clubId?: string }> = {}
        groups.forEach((g) => {
          (g.teams || []).forEach((t) => {
            if (t && t.teamId) {
              teamMap[t.teamId] = {
                teamId: t.teamId,
                name: t.name,
                clubId: t.clubId
              }
            }
          })
        })

        if (token !== this.loadToken) return
        this.seasonGroups = groups
        this.uniqueTeams = Object.values(teamMap)
      } catch (err) {
        console.error('SeasonDashboard loadSeason', err)
        if (token === this.loadToken) {
          this.seasonGroups = []
          this.uniqueTeams = []
        }
      } finally {
        if (token === this.loadToken) {
          this.loading = false
        }
      }
    }
  }
}
